(() => {
  'use strict';
  if (window.__printoraV27PredictiveSearchV2) return;
  window.__printoraV27PredictiveSearchV2 = true;

  const boot = () => {
    const form = document.querySelector('.pv27-search');
    const input = form?.querySelector('#pv27-search-input, input[type="search"][name="q"]');
    if (!form || !input || form.dataset.v27PredictiveReady === '2') return;
    form.dataset.v27PredictiveReady = '2';

    const box = document.createElement('div');
    box.className = 'pv27-search-results';
    box.id = 'pv27-search-results';
    box.setAttribute('role', 'listbox');
    box.setAttribute('aria-label', 'Search suggestions');
    form.appendChild(box);

    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', box.id);
    input.setAttribute('aria-expanded', 'false');

    let seed = { trendingTitle: 'Trending Now', trending: [], popularTitle: 'Popular Products', popularUrl: '/collections/all', products: [] };
    const seedNode = document.getElementById('pv27-search-seed');
    if (seedNode) {
      try {
        const parsed = JSON.parse(seedNode.textContent || '{}');
        seed = { ...seed, ...parsed };
      } catch (error) {
        console.warn('[Printora V27] Search seed could not be read', error);
      }
    }
    seed.trending = Array.isArray(seed.trending) ? seed.trending.map(value => String(value || '').trim()).filter(Boolean) : [];
    seed.products = Array.isArray(seed.products) ? seed.products.filter(product => product && product.title && product.url) : [];

    let timer = 0;
    let controller = null;
    let currentQuery = '';
    let activeIndex = -1;
    let navigable = [];

    const desktop = () => window.matchMedia('(min-width:1200px)').matches;
    const searchUrl = () => window.routes?.search_url || '/search';
    const currency = window.shop_currency || window.Shopify?.currency?.active || 'GBP';
    const money = new Intl.NumberFormat('en-GB', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const priceValue = product => {
      const raw = product?.price ?? product?.price_min ?? product?.variants?.[0]?.price;
      if (raw === null || raw === undefined || raw === '') return '';
      const number = Number(raw);
      if (!Number.isFinite(number)) return '';
      return money.format(number);
    };

    const imageValue = product => {
      const candidate = product?.featured_image?.url || product?.featured_image || product?.image?.url || product?.image || product?.images?.[0]?.url || product?.images?.[0] || '';
      return typeof candidate === 'string' ? candidate : '';
    };

    const productUrl = product => product?.url || (product?.handle ? `/products/${product.handle}` : '#');

    const close = () => {
      box.classList.remove('is-open');
      input.setAttribute('aria-expanded', 'false');
      activeIndex = -1;
      navigable = [];
    };

    const open = () => {
      box.classList.add('is-open');
      input.setAttribute('aria-expanded', 'true');
      if (window.innerWidth <= 749) {
        const rect = form.getBoundingClientRect();
        document.documentElement.style.setProperty('--pv27-search-mobile-top', `${Math.max(8, Math.round(rect.bottom + 8))}px`);
      }
    };

    const refreshNavigable = () => {
      navigable = [...box.querySelectorAll('[data-v27-search-nav]')];
      activeIndex = -1;
    };

    const setActive = next => {
      if (!navigable.length) return;
      activeIndex = (next + navigable.length) % navigable.length;
      navigable.forEach((node, index) => {
        const active = index === activeIndex;
        node.classList.toggle('is-active', active);
        if (active) node.setAttribute('aria-selected', 'true');
        else node.removeAttribute('aria-selected');
      });
      navigable[activeIndex]?.scrollIntoView({ block: 'nearest' });
    };

    const status = text => {
      box.replaceChildren();
      const row = document.createElement('div');
      row.className = 'pv27-search-results__status';
      row.textContent = text;
      box.appendChild(row);
      refreshNavigable();
      open();
    };

    const sectionTitle = text => {
      const title = document.createElement('h3');
      title.className = 'pv27-search-section__title';
      title.textContent = text;
      return title;
    };

    const renderSeed = () => {
      if (!desktop()) { close(); return; }
      box.replaceChildren();
      const scroll = document.createElement('div');
      scroll.className = 'pv27-search-results__scroll';

      if (seed.trending.length) {
        const section = document.createElement('section');
        section.className = 'pv27-search-section';
        section.appendChild(sectionTitle(seed.trendingTitle || 'Trending Now'));
        const trends = document.createElement('div');
        trends.className = 'pv27-search-trends';
        seed.trending.forEach(term => {
          const link = document.createElement('a');
          link.className = 'pv27-search-trend';
          link.href = `${searchUrl()}?q=${encodeURIComponent(term)}&type=product`;
          link.textContent = term;
          link.setAttribute('data-v27-search-nav', '');
          trends.appendChild(link);
        });
        section.appendChild(trends);
        scroll.appendChild(section);
      }

      if (seed.products.length) {
        const section = document.createElement('section');
        section.className = 'pv27-search-section';
        section.appendChild(sectionTitle(seed.popularTitle || 'Popular Products'));

        const popularWrap = document.createElement('div');
        popularWrap.className = 'pv27-search-popular-wrap';
        const grid = document.createElement('div');
        grid.className = 'pv27-search-popular-grid';
        seed.products.slice(0, 9).forEach(product => {
          const link = document.createElement('a');
          link.className = 'pv27-search-popular-card';
          link.href = product.url;
          link.setAttribute('data-v27-search-nav', '');

          const imageWrap = document.createElement('span');
          imageWrap.className = 'pv27-search-popular-card__image';
          if (product.sale) {
            const badge = document.createElement('span');
            badge.className = 'pv27-search-popular-card__badge';
            badge.textContent = 'Sale';
            imageWrap.appendChild(badge);
          }
          if (product.image) {
            const image = document.createElement('img');
            image.src = product.image;
            image.alt = '';
            image.loading = 'lazy';
            image.decoding = 'async';
            imageWrap.appendChild(image);
          }

          const title = document.createElement('span');
          title.className = 'pv27-search-popular-card__title';
          title.textContent = product.title;

          const price = document.createElement('span');
          price.className = 'pv27-search-popular-card__price';
          if (product.compareAt) {
            const compare = document.createElement('s');
            compare.textContent = product.compareAt;
            price.appendChild(compare);
          }
          const current = document.createElement('strong');
          current.textContent = product.price || '';
          price.appendChild(current);

          link.append(imageWrap, title, price);
          grid.appendChild(link);
        });
        popularWrap.appendChild(grid);
        section.appendChild(popularWrap);

        const footer = document.createElement('div');
        footer.className = 'pv27-search-popular-footer';
        const viewAll = document.createElement('a');
        viewAll.className = 'pv27-search-popular-viewall';
        viewAll.href = seed.popularUrl || '/collections/all';
        viewAll.textContent = 'View all popular products';
        viewAll.setAttribute('data-v27-search-nav', '');
        footer.appendChild(viewAll);
        section.appendChild(footer);

        scroll.appendChild(section);
      }

      if (!scroll.childElementCount) {
        const empty = document.createElement('div');
        empty.className = 'pv27-search-results__status';
        empty.textContent = 'Start typing to search Printora.';
        scroll.appendChild(empty);
      }

      box.appendChild(scroll);
      refreshNavigable();
      open();
    };

    const renderResults = (products, query) => {
      box.replaceChildren();
      const head = document.createElement('div');
      head.className = 'pv27-search-results__head';
      head.textContent = products.length ? 'Suggested products' : 'Search Printora';
      box.appendChild(head);

      if (products.length) {
        const list = document.createElement('div');
        list.className = 'pv27-search-results__list';
        products.slice(0, 6).forEach(product => {
          const link = document.createElement('a');
          link.className = 'pv27-search-result';
          link.href = productUrl(product);
          link.setAttribute('data-v27-search-nav', '');

          const image = document.createElement('span');
          image.className = 'pv27-search-result__image';
          const src = imageValue(product);
          if (src) {
            const img = document.createElement('img');
            img.src = src;
            img.alt = '';
            img.loading = 'lazy';
            img.decoding = 'async';
            image.appendChild(img);
          }

          const title = document.createElement('span');
          title.className = 'pv27-search-result__title';
          title.textContent = product?.title || 'Product';

          const price = document.createElement('span');
          price.className = 'pv27-search-result__price';
          price.textContent = priceValue(product);

          link.append(image, title, price);
          list.appendChild(link);
        });
        box.appendChild(list);
      } else {
        const empty = document.createElement('div');
        empty.className = 'pv27-search-results__status';
        empty.textContent = `No products found for “${query}”`;
        box.appendChild(empty);
      }

      const all = document.createElement('a');
      all.className = 'pv27-search-results__all';
      all.href = `${searchUrl()}?q=${encodeURIComponent(query)}&type=product`;
      all.setAttribute('data-v27-search-nav', '');
      const copy = document.createElement('span');
      copy.textContent = `View all results for “${query}”`;
      const arrow = document.createElement('span');
      arrow.textContent = '→';
      all.append(copy, arrow);
      box.appendChild(all);

      refreshNavigable();
      open();
    };

    const endpoint = () => {
      const configured = window.routes?.predictive_search_url || '/search/suggest';
      return configured.endsWith('.json') ? configured : `${configured}.json`;
    };

    const search = async query => {
      controller?.abort();
      controller = new AbortController();
      currentQuery = query;
      status('Searching…');
      try {
        const params = new URLSearchParams();
        params.set('q', query);
        params.set('resources[type]', 'product');
        params.set('resources[limit]', '6');
        params.set('resources[options][unavailable_products]', 'last');
        const response = await fetch(`${endpoint()}?${params.toString()}`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: controller.signal
        });
        if (!response.ok) throw new Error(`Search failed: ${response.status}`);
        const payload = await response.json();
        if (query !== currentQuery) return;
        const products = payload?.resources?.results?.products || [];
        renderResults(Array.isArray(products) ? products : [], query);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.warn('[Printora V27] Predictive search failed', error);
        if (query === currentQuery) status('Keep typing, or press Enter to search.');
      }
    };

    input.addEventListener('input', () => {
      const query = input.value.trim();
      window.clearTimeout(timer);
      controller?.abort();
      currentQuery = query;
      if (!query) {
        if (desktop()) renderSeed();
        else close();
        return;
      }
      timer = window.setTimeout(() => search(query), 150);
    });

    input.addEventListener('focus', () => {
      const query = input.value.trim();
      if (!query && desktop()) renderSeed();
      else if (query && box.childElementCount) open();
      else if (query) search(query);
    });

    input.addEventListener('keydown', event => {
      if (!box.classList.contains('is-open')) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActive(activeIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActive(activeIndex <= 0 ? navigable.length - 1 : activeIndex - 1);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        close();
      } else if (event.key === 'Enter' && activeIndex >= 0 && navigable[activeIndex]) {
        event.preventDefault();
        window.location.href = navigable[activeIndex].href;
      }
    });

    document.addEventListener('pointerdown', event => {
      if (!form.contains(event.target)) close();
    });

    window.addEventListener('resize', () => {
      if (!box.classList.contains('is-open')) return;
      if (!desktop() && !input.value.trim()) close();
      else open();
    }, { passive: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
